import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import { Term } from './term'
import { Type } from './type'

type EnumBase<TId extends Enum.Id = Enum.Id> = Model<TId>
export interface Enum<TId extends Enum.Id = Enum.Id> extends EnumBase<TId> {
  entries: readonly Enum.Entry[]
}
export namespace Enum {
  export const isEnum: TypeChecker<Enum> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.intersection(
      Model.getTypeChecker(Id.isId),
      TypeChecker.Factory.object<Difference<Enum, EnumBase>>({
        entries: TypeChecker.Factory.list(Entry.isEntry),
      }),
    ),
  ).usingAlias('dms.models.Enum')
  /**
   * Just checks if the object's ID indicates that it is a purpose. This can be
   * used to distinguish between various model types without checking that every
   * field of the model is correct.
   */
  export const shouldBeEnum = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Enum),
  )

  /**
   * Based on {@link common.value.types.Enum.Entry}, but specific to the DMS
   */
  export type Entry = common.value.types.Enum.Entry
  export namespace Entry {
    export const isEntry: TypeChecker<Entry> = TypeChecker.Factory.lazy(
      () => common.value.types.Enum.Entry.isEntry,
    ).usingAlias('dms.models.Enum.Entry')

    /**
     * Based on {@link common.value.types.Enum.Entry.Id}, but specific to the DMS
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export import Id = common.value.types.Enum.Entry.Id

    /**
     * Converts an {@link Entry.Input} into an {@link Entry}
     */
    export function from(input: Input): Entry {
      return {
        ...input,
        id: input.id ?? common.value.types.Enum.Entry.Id.generate(),
      }
    }

    /**
     * Information needed to create or update an {@link Entry}
     *
     * If {@link Input.id} is provided and corresponds to an existing
     * {@link Entry}, this information will be used to update that
     * {@link Entry}. Otherwise, a new {@link Entry} will be created. If
     * {@link Input.id} was not provided, it will be generated.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Input = Partial.Semi<Entry, 'id'>
    export namespace Input {
      export const isInput: TypeChecker<Input> = TypeChecker.Factory.omit(
        isEntry,
        ['id'],
      ).usingAlias('dms.models.Enum.Entry.Input')
    }
  }

  export interface Input extends Omit<Enum, 'id' | 'entries' | 'terms'> {
    /**
     * @default []
     */
    entries?: readonly Entry.Input[]
    /**
     * @default PartialId.Custom.create()
     */
    partialId?: PartialId.Standard
    /**
     * @default []
     */
    termIds?: readonly Term.Id[]
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isEnum, ['id']),
        TypeChecker.Factory.object<Difference<Input, Enum>>({
          entries: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Entry.Input.isInput),
          ),
          partialId: TypeChecker.Factory.optional(
            PartialId.Standard.isStandard,
          ),
          termIds: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Term.Id.isId),
          ),
        }),
      ),
    ).usingAlias('dms.models.Enum.Input')
  }

  export type Updates = Partial<Omit<Input, 'partialId'>>
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(isEnum, ['id']),
          TypeChecker.Factory.object<Difference<Updates, Input>>({
            name: TypeChecker.Factory.optional(TypeChecker.isString),
          }),
        ),
    ).usingAlias('dms.models.Enum.Updates')
  }

  export type Identifiable<TPartialId extends PartialId = PartialId> =
    _Identifiable.ForSpecificType<Type.Enum, TPartialId, Id.Context>
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        _Identifiable.ForSpecificType.getTypeChecker(
          Type.Enum,
          Id.Context.isContext,
        ),
      ).usingAlias('dms.models.Enum.Identifiable')
  }

  export type Id<TPartialId extends PartialId = PartialId> = _Id<
    Type.Enum,
    TPartialId,
    Id.Context
  >
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Enum, Context.isContext),
    ).usingAlias('dms.models.Enum.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId>
        ? Id<TPartialId>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(source: Identifiable): Id {
      return _Id.from<Type.Enum, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Enum),
      )
    }

    export type Context = _Id.Context.None
    export namespace Context {
      export const isContext: TypeChecker<Context> =
        _Id.Context.None.isNone.usingAlias('dms.models.Enum.Id.Context')
    }
  }

  /**
   * Creates a {@link common.value.types.Enum} from a {@link Enum} model
   */
  export function toCommonEnum({
    enum: _enum,
    isMultiselect,
  }: {
    enum: Enum
    isMultiselect?: boolean
  }): common.value.types.Enum {
    return new common.value.types.Enum({
      entries: _enum.entries,
      isMultiselect,
    })
  }
}
